import React, { useEffect, useState } from 'react';
import DottedOptionsSVG from 'components/svgs/DottedOptions';
import { POLL_STATUS } from 'modules/AnchorAPI/v3/episodes/fetchInteractivityPoll';
import { useDeletePoll, useUpdatePoll } from 'hooks/useInteractivityPolls';
import { MenuItemIconWrapper } from 'screens/EpisodeEditorScreen/styles';
import { Icon } from 'shared/Icon';
import { ConfirmationModal } from 'components/EpisodeEditorPublish/components/InteractivePoll/ConfirmationModal';
import { useT } from 'client/i18n/hooks/useT';
import {
  CircularIconContainer,
  ThreeDotMenuContainer,
  ThreeDotMenuItem,
} from './styles';

export function ThreeDotMenu({
  episodeId,
  userId,
  status,
}: {
  episodeId: string;
  userId: number;
  status?: POLL_STATUS;
}) {
  const { deletePoll, status: deleteStatus } = useDeletePoll();
  const { updatePoll, status: updateStatus } = useUpdatePoll();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [confirmationAction, setConfirmationAction] = useState<
    'delete' | 'end' | undefined
  >();
  const t = useT();

  const toggleOpen = () => setIsMenuOpen(!isMenuOpen);

  useEffect(() => {
    if (updateStatus === 'idle' && deleteStatus === 'idle') {
      setConfirmationAction(undefined);
    }
  }, [updateStatus, deleteStatus]);

  return (
    <ThreeDotMenuContainer
      id="three-dot"
      title={<DottedOptionsSVG />}
      noCaret
      pullRight
      onClick={toggleOpen}
      open={isMenuOpen}
    >
      {status && status !== POLL_STATUS.DRAFT && (
        <li role="presentation">
          <ThreeDotMenuItem
            href={`/api/proxy/v3/episodes/webEpisodeId:${episodeId}/poll?results=true`}
            onClick={toggleOpen}
            target="_blank"
            rel="noopener noreferrer"
          >
            <MenuItemIconWrapper>
              <Icon
                type="download"
                fillColor="#CCCDCF"
                isInCircle={false}
                circleColor="#fff"
              />
            </MenuItemIconWrapper>
            {t(
              'EpisodeEditorPublish.InteractivePoll.ThreeDotMenu.ViewResultsMenuItem',
              'View results',
              'View poll results context menu option label'
            )}
          </ThreeDotMenuItem>
        </li>
      )}
      {status === POLL_STATUS.LIVE && (
        <li role="presentation">
          <ThreeDotMenuItem
            onClick={() => {
              setConfirmationAction('end');
              toggleOpen();
              setIsConfirmationOpen(true);
            }}
            type="button"
          >
            <MenuItemIconWrapper>
              <CircularIconContainer>
                <Icon
                  type="x"
                  fillColor="#FFF"
                  isInCircle={true}
                  circleColor="#cccdcf"
                />
              </CircularIconContainer>
            </MenuItemIconWrapper>{' '}
            {updateStatus === 'loading'
              ? t(
                  'EpisodeEditorPublish.InteractivePoll.ThreeDotMenu.EndingPollMenuItem',
                  'Ending poll ...',
                  'Ending poll context menu option label'
                )
              : t(
                  'EpisodeEditorPublish.InteractivePoll.ThreeDotMenu.EndPollMenuItem',
                  'End Poll',
                  'End poll context menu option label'
                )}
          </ThreeDotMenuItem>
        </li>
      )}
      <li role="presentation">
        <ThreeDotMenuItem
          onClick={() => {
            setConfirmationAction('delete');
            toggleOpen();
            setIsConfirmationOpen(true);
          }}
          isWarning={true}
          type="button"
        >
          <MenuItemIconWrapper>
            <Icon
              type="trash"
              fillColor="#CCCDCF"
              isInCircle={false}
              circleColor=""
            />
          </MenuItemIconWrapper>{' '}
          {deleteStatus === 'loading'
            ? t(
                'EpisodeEditorPublish.InteractivePoll.ThreeDotMenu.DeletingPollMenuItem',
                'Deleting poll ...',
                'Deleting poll context menu option label'
              )
            : t(
                'EpisodeEditorPublish.InteractivePoll.ThreeDotMenu.DeletePollMenuItem',
                'Delete Poll',
                'Delete poll context menu option label'
              )}
        </ThreeDotMenuItem>
      </li>
      {isConfirmationOpen && (
        <ConfirmationModal
          action={confirmationAction}
          onCancel={() => {
            setIsConfirmationOpen(false);
            setIsMenuOpen(false);
          }}
          onConfirm={() => {
            setIsConfirmationOpen(false);
            return confirmationAction === 'end'
              ? onClosePoll()
              : onDeletePoll();
          }}
        />
      )}
    </ThreeDotMenuContainer>
  );

  async function onDeletePoll() {
    try {
      deletePoll(
        { episodeId, userId },
        {
          onSuccess: toggleOpen,
          onError: toggleOpen,
        }
      );
    } catch (e) {
      throw new Error(`Couldn't delete poll; error: ${e}`);
    }
  }

  async function onClosePoll() {
    try {
      updatePoll(
        {
          episodeId,
          poll: {
            // eslint-disable-next-line camelcase
            closing_date: new Date().getTime(),
          },
        },
        {
          onSuccess: toggleOpen,
          onError: toggleOpen,
        }
      );
    } catch (e) {
      throw new Error(`Couldn't close poll; error: ${e}`);
    }
  }
}
